import UndertypeSelector from "@/Components/Selectors/UndertypeSelector";
import { COUNTRIES_LIST, ICountryItem } from "@/Constants/CountriesList";
import CountryFilter from "@/Pages/MyAppsPage/Modules/AppsCardsList/Modals/AppStatisticModal/CountryFilter/CountryFilter";
import basicElements from "@/Theme/basicElements.module.scss";
import modalElements from "@/Theme/modalsElements.module.scss";
import { clsx } from "clsx";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Loader } from "rsuite";
import { utils, writeFile } from "xlsx";
import DateSelector from "../../../../../../Components/DateSelector";
import { IAppItem } from "../../../../../../Models/ApiModels";
import {
  IStatisticResponse,
  TStatisticType,
  TStatisticUndertype,
  useGetAppStatisticQuery,
} from "../../../../../../Store/APIs/Apps.api";
import BasicButton from "../../../../../../UI/BasicButton";
import BasicStoreIcon from "../../../../../../UI/icons/BasicStoreIcon";
import RemoveCrossButton from "../../../../../../UI/RemoveCrossButton";
import {
  getFormattedDate,
  getOneWeekAgo,
} from "../../../../../../Utils/DateUtils";
import styles from "./AppStatisticModal.module.scss";

const statTypes: Record<TStatisticType, string> = {
  install: "Installs",
  rate: "Rates",
  "review-manual": "Reviews",
};

const statUndertypes: Record<TStatisticUndertype, string> = {
  default: "Installs",
  views: "Additional Impressions",
};

interface IStatisticProps {
  appInfo: IAppItem;
  onClose: () => void;
}

const AppStatisticModal = React.forwardRef<HTMLDivElement, IStatisticProps>(
  ({ appInfo, onClose }, ref) => {
    const [dateStart, setDateStart] = useState(
      getOneWeekAgo(getFormattedDate(new Date())),
    );
    const [dateStop, setDateStop] = useState(getFormattedDate(new Date()));
    const [countriesList, setCountriesList] = useState<ICountryItem[]>([]);
    const [selectedCountriesList, setSelectedCountriesList] = useState<
      ICountryItem[]
    >([]);
    const [dataExport, setDataExport] = useState<IStatisticResponse[]>([]);
    const [currentType, setCurrentType] = useState<TStatisticType>("install");
    const [currentUndertype, setCurrentUndertype] =
      useState<TStatisticUndertype>("default");

    const {
      data = [],
      isLoading,
      refetch,
    } = useGetAppStatisticQuery({
      id: appInfo.id,
      dateStart: dateStart,
      dateStop: dateStop,
      type: currentType,
      ...(currentType === "install" && { undertype: currentUndertype }),
    });

    useEffect(() => {
      refetch();
    }, [currentType, refetch]);

    useEffect(() => {
      if (data.length > 0) {
        const newCountriesISO = Array.from(
          new Set(data.map((row) => row.country)),
        );
        const newCountriesList: ICountryItem[] = newCountriesISO.map(
          (ISO: string) =>
            COUNTRIES_LIST.find(
              (country) => country.ISO === ISO,
            ) as ICountryItem,
        );

        setCountriesList(newCountriesList);
      }
    }, [data]);

    const handleSelectStatisticType = (label: string) => {
      const newType = Object.entries(statTypes).find(
        ([, value]) => value === label,
      )?.[0] as TStatisticType;

      setCurrentType(newType);
      setCurrentUndertype("default");
    };

    const handleSelectStatisticUnderype = (label: string) => {
      const newUndertype = Object.entries(statUndertypes).find(
        ([, value]) => value === label,
      )?.[0] as TStatisticUndertype;
      setCurrentUndertype(newUndertype);
    };

    const handleSetDateStart = (date: string) => {
      setDateStart(date);
    };

    const handleSetDateStop = (date: string) => {
      setDateStop(date);
    };

    const handleExport = () => {
      if (data.length) {
        const headerRow = [
          "Keys",
          ...Object.keys(dataExport[0]).filter((key) => key !== "key"),
        ];
        const worksheetData = [headerRow];
        dataExport.forEach((obj) => {
          const row = [obj.key];
          Object.keys(obj).forEach((key) => {
            if (key !== "key") {
              row.push(obj[key]);
            }
          });
          worksheetData.push(row);
        });

        const ws = utils.aoa_to_sheet(worksheetData);
        const wb = utils.book_new();

        utils.book_append_sheet(wb, ws, `Data`);

        const titleType =
          currentType === "rate"
            ? "rates"
            : currentUndertype === "default"
              ? "installs"
              : "impressions";

        writeFile(
          wb,
          `${titleType}-statistic_report_${
            appInfo.url.includes("apple") ? "AppStore" : "PlayMarket"
          }_${appInfo.name.replace(/ /g, "_")}_${moment(dateStart).format(
            "YYYY-MM-DD",
          )}_-_${moment(dateStop).format("YYYY-MM-DD")}.xlsx`,
        );
      }
    };

    const handleCountrySelect = (ISO: string | null) => {
      const countryObject = COUNTRIES_LIST.find(
        (country) => country.ISO === ISO,
      );
      const countryInState = Boolean(
        selectedCountriesList.find((country) => country.ISO === ISO),
      );

      if (countryObject && !countryInState) {
        setSelectedCountriesList([...selectedCountriesList, countryObject]);
      }
    };

    const handleCountryDelete = (ISO: string) => {
      setSelectedCountriesList((prevState) =>
        prevState.filter((country) => country.ISO !== ISO),
      );
    };

    const memoizedTable = useMemo(() => {
      if (!data.length) {
        return <></>;
      }

      if (currentType === "review-manual") {
        let filteredData: IStatisticResponse[] = [];
        if (!selectedCountriesList.length) {
          filteredData = data;
        } else {
          selectedCountriesList.forEach((country) => {
            filteredData = [
              ...filteredData,
              ...data.filter((row) => row.country === country.ISO),
            ];
          });
        }
        setDataExport(filteredData);
        return (
          <div className={styles.table}>
            {filteredData.length ? (
              <table>
                <thead>
                  <tr>
                    <th
                      className={clsx(
                        basicElements.basic__table_cell,
                        styles.th,
                      )}
                    >
                      Country
                    </th>
                    <th
                      className={clsx(
                        basicElements.basic__table_cell,
                        styles.th,
                      )}
                    >
                      Date
                    </th>
                    <th
                      className={clsx(
                        basicElements.basic__table_cell,
                        styles.th,
                      )}
                    >
                      Rate
                    </th>
                    <th
                      className={clsx(
                        basicElements.basic__table_cell,
                        styles.th,
                      )}
                    >
                      Title
                    </th>
                    <th
                      className={clsx(
                        basicElements.basic__table_cell,
                        styles.th,
                      )}
                    >
                      Content
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row, idx) => (
                    <tr
                      key={`${row.country}-${row.date}-${idx}`}
                      className={clsx(idx % 2 !== 0 ? styles.evenRow : "")}
                    >
                      <td
                        className={clsx(
                          basicElements.basic__table_cell,
                          styles.td,
                        )}
                      >
                        {row.country}
                      </td>
                      <td
                        className={clsx(
                          basicElements.basic__table_cell,
                          styles.td,
                        )}
                      >
                        {row.date}
                      </td>
                      <td
                        className={clsx(
                          basicElements.basic__table_cell,
                          styles.td,
                        )}
                      >
                        {row.rate}
                      </td>
                      <td
                        className={clsx(
                          basicElements.basic__table_cell,
                          styles.td,
                          styles.reviewTitle,
                        )}
                      >
                        {row.title}
                      </td>
                      <td
                        className={clsx(
                          basicElements.basic__table_cell,
                          styles.td,
                          styles.reviewContent,
                        )}
                      >
                        {row.review}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <span className={styles.emptyText}>No data</span>
            )}
          </div>
        );
      }

      let filteredData: IStatisticResponse[] = [];
      const dateList = Object.keys(data[0]).filter((key) => key !== "key");

      if (!selectedCountriesList.length) {
        filteredData = data;
      } else {
        selectedCountriesList.map((country) => {
          const filteredCountryData = data.filter(
            (row) => row.country === country.ISO,
          );
          filteredData = [...filteredData, ...filteredCountryData];
        });
      }

      const groupedData: { [country: string]: IStatisticResponse[] } = {};
      filteredData.forEach((row) => {
        if (!groupedData[row.country]) {
          groupedData[row.country] = [];
        }
        groupedData[row.country].push(row);
      });

      const countryTotals: { [key: string]: { [date: string]: number } } = {};
      const globalTotals: { [date: string]: number } = {};

      filteredData.forEach((row) => {
        if (!countryTotals[row.country]) {
          countryTotals[row.country] = {};
        }

        dateList.forEach((date) => {
          if (date !== "country") {
            const value = parseFloat(row[date]) || 0;
            countryTotals[row.country][date] =
              (countryTotals[row.country][date] || 0) + value;
            globalTotals[date] = (globalTotals[date] || 0) + value;
          }
        });
      });

      setDataExport(filteredData);

      const allRows: JSX.Element[] = [];
      Object.keys(groupedData).forEach((country, countryIndex) => {
        groupedData[country].forEach((stat, index) => {
          allRows.push(
            <tr key={`${stat.key}-${stat.country}-${index}`}>
              <td
                className={clsx(basicElements.basic__table_cell, styles.td)}
                data-role="country"
              >
                {stat.country}
              </td>
              <td
                className={clsx(basicElements.basic__table_cell, styles.td)}
                data-role="key"
              >
                {stat.key}
              </td>
              {dateList?.map(
                (date) =>
                  date !== "country" && (
                    <td
                      className={clsx(
                        basicElements.basic__table_cell,
                        styles.td,
                      )}
                      key={`${stat.key}-${date}-${stat.country}`}
                    >
                      {stat[date]}
                    </td>
                  ),
              )}
            </tr>,
          );
        });

        // итог по стране, если выбрано больше одной страны или не выбрано ни одной
        if (selectedCountriesList.length !== 1) {
          allRows.push(
            <tr key={`total-${country}`}>
              <td
                className={clsx(basicElements.basic__table_cell, styles.td)}
                data-role="country"
                data-total="true"
              >
                {country}
              </td>
              <td
                className={clsx(basicElements.basic__table_cell, styles.td)}
                data-role="key"
                data-total="true"
              >
                Total
              </td>
              {dateList?.map(
                (date) =>
                  date !== "country" && (
                    <td
                      className={clsx(
                        basicElements.basic__table_cell,
                        styles.td,
                      )}
                      key={`total-${country}-${date}`}
                      data-total="true"
                    >
                      {countryTotals[country][date]}
                    </td>
                  ),
              )}
            </tr>,
          );
        }
      });

      // общий итог
      allRows.push(
        <tr key="global-total">
          <td
            className={clsx(
              basicElements.basic__table_cell,
              styles.td,
              styles.totalRow,
            )}
            data-total="true"
          >
            All Countries
          </td>
          <td
            className={clsx(
              basicElements.basic__table_cell,
              styles.td,
              styles.totalRow,
            )}
            data-total="true"
          >
            Total
          </td>
          {dateList?.map(
            (date) =>
              date !== "country" && (
                <td
                  className={clsx(
                    basicElements.basic__table_cell,
                    styles.td,
                    styles.totalRow,
                  )}
                  key={`global-total-${date}`}
                  data-total="true"
                >
                  {globalTotals[date]}
                </td>
              ),
          )}
        </tr>,
      );

      const rowsWithClasses = allRows.map((row, index) => {
        if (row.props["data-total"] === "true") {
          return row;
        }
        return React.cloneElement(row, {
          className: index % 2 !== 0 ? styles.evenRow : "",
        });
      });

      return (
        <div className={styles.table}>
          {filteredData.length ? (
            <table>
              <thead>
                <tr>
                  <th
                    className={clsx(basicElements.basic__table_cell, styles.th)}
                  >
                    Country
                  </th>
                  <th
                    className={clsx(basicElements.basic__table_cell, styles.th)}
                  >
                    Keys
                  </th>
                  {dateList?.map(
                    (date) =>
                      date !== "country" && (
                        <th
                          className={clsx(
                            basicElements.basic__table_cell,
                            styles.th,
                          )}
                          key={date}
                        >
                          {date}
                        </th>
                      ),
                  )}
                </tr>
              </thead>
              <tbody>{rowsWithClasses}</tbody>
            </table>
          ) : (
            <span className={styles.emptyText}>No data</span>
          )}
        </div>
      );
    }, [data, selectedCountriesList, currentType]);

    return (
      <div
        className={clsx(
          styles.wrapper,
          modalElements.modal_wrapper,
          modalElements.modal_with_tabs,
        )}
        ref={ref}
        tabIndex={-1}
      >
        <div className={modalElements.modal_tabs_list}>
          {Object.values(statTypes).map((tab) => (
            <div
              key={tab}
              className={clsx(modalElements.modal_tab, {
                [modalElements.modal_tab_active]:
                  statTypes[currentType] === tab,
              })}
              onClick={() => handleSelectStatisticType(tab as TStatisticType)}
            >
              {tab}
            </div>
          ))}
        </div>
        <div className={modalElements.close_button_block}>
          <RemoveCrossButton onClick={onClose} />
        </div>
        <div className={styles.titleWrapper}>
          <BasicStoreIcon
            store={appInfo.url.includes("apple") ? "apple" : "google"}
          />
          <h3 className={clsx(modalElements.modal_title, styles.title)}>
            {appInfo.name} statistics
          </h3>
        </div>
        {isLoading ? (
          <Loader center size="lg" />
        ) : (
          <>
            <div className={styles.controlWrapper}>
              <DateSelector
                dateStart={dateStart}
                dateStop={dateStop}
                handleGetDateStop={handleSetDateStop}
                handleGetDateStart={handleSetDateStart}
                cleanable={false}
                dateRange={60}
              />
              <BasicButton
                onClick={handleExport}
                size="md"
                text="Export XLSX"
                variant="blue"
                disabled={!data.length}
              />
            </div>

            <CountryFilter
              selectedCountriesList={selectedCountriesList}
              countriesList={countriesList}
              handleCountrySelect={handleCountrySelect}
              handleCountryDelete={handleCountryDelete}
            />
            {currentType === "install" ? (
              <UndertypeSelector
                selected={statUndertypes[currentUndertype]}
                onSelectType={handleSelectStatisticUnderype}
                list={Object.values(statUndertypes)}
              />
            ) : null}
            {memoizedTable}
          </>
        )}
      </div>
    );
  },
);

export default AppStatisticModal;
